import React from "react";
import type { Theme } from "./theme";
import { sendChat, type ChatTurn } from "./api";

/**
 * Anivaze v4 — learning CHAT panel (PRODUCT-CHAT-EDIT §1). Converse to understand
 * a topic (guardrailed, free model), then hand it to the generator with "Make a
 * video". Refuse/redirect replies from the guardrail render as visible boundary
 * bubbles and never become the "Make a video" topic. The panel stays in the
 * chrome's own neutrals — the canvas theme stays on the canvas (contrast is
 * computed against ONE surface, not seven).
 */

type Turn = ChatTurn & { action?: "allow" | "refuse" | "redirect" };

export const Chat: React.FC<{ theme: Theme; onMakeVideo: (topic: string) => void }> = ({ onMakeVideo }) => {
  const [messages, setMessages] = React.useState<Turn[]>([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [lastTopic, setLastTopic] = React.useState("");
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    scrollRef.current?.scrollTo({ top: 1e9, behavior: reduce ? "auto" : "smooth" });
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next: Turn[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const res = await sendChat(next);
      setMessages([...next, { role: "assistant", content: res.reply || "…", action: res.action }]);
      // Only an ALLOWED, substantial message becomes the video topic — a refused
      // ask or a follow-up like "why?" must not end up on the 🎬 button.
      if (res.action === "allow" && text.split(/\s+/).length >= 3) setLastTopic(text);
    } catch {
      setMessages([...next, { role: "assistant", content: "Couldn't reach the chat service — check your connection and resend. You can still make the video: press the 🎬 button below." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, height: "100%", minHeight: 320 }}>
      <div ref={scrollRef} role="log" aria-live="polite" style={S.thread}>
        {messages.length === 0 && (
          <div style={{ ...S.hint, textAlign: "center", marginTop: 24 }}>
            Ask about any topic you want to learn — “how do vaccines work?”, “teach me compound interest”. After you ask, a 🎬 Make a video button appears below.
          </div>
        )}
        {messages.map((m, i) => {
          const boundary = m.role === "assistant" && (m.action === "refuse" || m.action === "redirect");
          return (
            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
              <div
                className={m.role === "assistant" ? "chat-in" : undefined}
                style={{
                  ...S.bubble,
                  ...(m.role === "user"
                    ? S.userBubble
                    : boundary
                      ? S.boundaryBubble
                      : S.assistantBubble),
                }}
              >
                {boundary && <div style={S.boundaryTag}>Outside Anivaze’s learning scope</div>}
                {m.content}
              </div>
            </div>
          );
        })}
        {busy && <div role="status" style={S.hint}>thinking…</div>}
      </div>

      {lastTopic && (
        <button className="v4-btn-primary v4-pop-in" onClick={() => onMakeVideo(lastTopic)} style={S.makeBtn}>
          🎬 Make a video about “{lastTopic.length > 40 ? lastTopic.slice(0, 40) + "…" : lastTopic}”
        </button>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <input
          className="v4-input v4-field"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="Ask to learn something…"
          aria-label="Ask to learn something"
          style={{ ...S.input, flex: 1 }}
        />
        <button className="v4-btn-primary" onClick={send} disabled={busy} style={S.sendBtn}>{busy ? "…" : "Send"}</button>
      </div>
    </div>
  );
};

/* Chrome neutrals only — matches the Storyboard card system (#0f1216 well,
 * #1d2329 raised, #ff5d33 accent with dark ink at 5.98:1). */
const S: Record<string, React.CSSProperties> = {
  thread: { flex: 1, overflowY: "auto", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 8, background: "#0f1216" },
  bubble: { maxWidth: "82%", padding: "9px 12px", borderRadius: 14, fontSize: 14, lineHeight: 1.45, whiteSpace: "pre-wrap" },
  userBubble: { background: "#ff5d33", color: "#12151a", border: "none" },
  assistantBubble: { background: "#1d2329", color: "#e6eaef", border: "1px solid #2a323b" },
  boundaryBubble: { background: "#231a12", color: "#ffd9cc", border: "1px solid #7a4a33" },
  boundaryTag: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#e8b34a", marginBottom: 4 },
  hint: { fontSize: 12.5, color: "#8a939e", padding: "4px 8px" },
  makeBtn: { border: "none", background: "#ff5d33", color: "#12151a", borderRadius: 10, padding: "10px 12px", fontSize: 14, fontWeight: 700, cursor: "pointer", minHeight: 40 },
  input: { border: "1px solid #262c34", background: "#0f1216", color: "#e6eaef", borderRadius: 10, padding: "10px 12px", fontSize: 16 },
  sendBtn: { border: "none", background: "#ff5d33", color: "#12151a", borderRadius: 10, padding: "10px 16px", fontSize: 14, fontWeight: 700, cursor: "pointer", minHeight: 40 },
};
